import {
  DeviceUseStatementCode,
  type DeviceUseStatementStatus,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import type { Admission } from '../admission/schema';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import {
  type PaginationQueryParams,
  PaginationSchema,
} from '../domain/pagination';
import { PaginatedBundleSchema, PeriodSchema } from '../domain/schema';
import type { WithId } from '../types';

const deviceAssociationStatus = [
  'active',
  'completed',
  'entered-in-error',
  'intended',
  'stopped',
  'on-hold',
] as const;

export const DeviceAssociationStatusSchema = z.enum(deviceAssociationStatus);

export const DeviceAssociationCodeSchema = z.nativeEnum(DeviceUseStatementCode);

export const DeviceAssociationSchema = z.object({
  patient: DisplayReferenceSchema,
  device: DisplayReferenceSchema,
  code: DeviceAssociationCodeSchema,
  status: DeviceAssociationStatusSchema,
  period: PeriodSchema,
});

const searchParamSchema = z.object({
  device: z.string().optional(),
  subject: z.string().optional(),
});

const tableStatement = z.object({
  id: z.string().uuid(),
  start: z.date(),
  end: z.date(),
  type: z.string(),
  code: DeviceAssociationCodeSchema,
  device: DisplayReferenceSchema,
  patient: DisplayReferenceSchema,
  isActive: z.boolean(),
  status: DeviceAssociationStatusSchema,
});

export const DeviceAssociationGetResponseSchema =
  DeviceAssociationSchema.partial().extend({
    id: UUIDSchema,
  });

/**
 * Omit the subject from the admission since that requires the full patient resource to be fetched
 * which isn't necessary when viewing things at the device use statement level
 */
type DeviceAssociationAdmission = Omit<Admission, 'subject'>;

export interface DeviceAssociationWithLinkedResources
  extends DeviceAssociation {
  inProgressAdmission: WithId<Partial<DeviceAssociationAdmission>>;
}

export const DeviceAssociationWithIdSchema =
  DeviceAssociationSchema.partial().extend({
    id: z.string().uuid(),
  });
export type DeviceAssociationWithId = z.infer<
  typeof DeviceAssociationWithIdSchema
>;

export const DeviceAssociationCreateRequestSchema = z.object({
  device: UUIDSchema,
  patient: UUIDSchema,
});
export type DeviceAssociationCreateRequest = z.infer<
  typeof DeviceAssociationCreateRequestSchema
>;

export type DeviceAssociationCreateResponse = DeviceAssociationWithId;

export interface DeviceAssociationGetAllParams extends PaginationQueryParams {
  status?: DeviceUseStatementStatus;
  code?: DeviceUseStatementCode;
  device?: string;
  sort?: string;
}

export const DeviceAssociationGetAllParamsSchema = PaginatedBundleSchema.extend(
  {
    associations: DeviceAssociationGetResponseSchema.array(),
  },
);

export type DeviceAssociationTable = z.infer<typeof tableStatement>;

export const DeviceAssociationSearchSchema =
  PaginationSchema.merge(searchParamSchema);

export type DeviceAssociationSearchParams = z.infer<
  typeof DeviceAssociationSearchSchema
>;

export type DeviceAssociation = z.infer<typeof DeviceAssociationSchema>;

export type DeviceAssociationCreate = DeviceAssociation;

export type DeviceAssociationGetResponse = z.infer<
  typeof DeviceAssociationGetResponseSchema
>;

export type DeviceAssociationWithAdmissionGetResponse = WithId<
  Partial<DeviceAssociationWithLinkedResources>
>;

export type DeviceAssociationGetAllResponse = z.infer<
  typeof DeviceAssociationGetAllParamsSchema
>;
